import type { ReactElement } from 'react';
import { Container, Grid, Typography } from '@mui/material';
import { AppLogo, List, ListItem, ListItemPrimaryText } from '@noah-labs/fe-shared-ui-components';
import type {
  MarkdownRemarkFrontmatterFooterLabels,
  MarkdownRemarkFrontmatterHeaderMenuHeaderLinks,
  MarkdownRemarkFrontmatterSocialMenu,
  Maybe,
} from '../../../generated/types';
import { BackToTopButton } from '../buttons/BackToTopButton';
import { NewsletterForm } from '../forms/NewsletterForm';
import { NavigationItem } from '../menus/NavigationItem';
import { SocialMenu } from '../menus/SocialMenu';

export type PpFooter = {
  disclaimer?: Maybe<string>;
  footerLabels?: MarkdownRemarkFrontmatterFooterLabels;
  footerMenu?: MarkdownRemarkFrontmatterHeaderMenuHeaderLinks[] | null | undefined;
  legalCompanyName?: Maybe<string>;
  legalPrefix?: Maybe<string>;
  socialMenu?: MarkdownRemarkFrontmatterSocialMenu | null | undefined;
  tagline1?: Maybe<string>;
  tagline2?: Maybe<string>;
};

export function Footer({
  disclaimer,
  footerLabels,
  footerMenu,
  legalCompanyName,
  legalPrefix,
  socialMenu,
  tagline1 = 'Global Payments.',
  tagline2 = 'Made simple.',
}: PpFooter): ReactElement {
  const currentYear = new Date().getFullYear();

  const menuItemsWithSublink = footerMenu?.filter((menu) => menu.headerURL === 'blank');

  return (
    <Container disableGutters component="footer" maxWidth="lg">
      <Grid container columnSpacing={2} padding={3} rowSpacing={4}>
        <Grid order={1} size={{ md: 2, sm: 6, xs: 12 }}>
          <Typography variant="subHeadingM">{tagline1}</Typography>
          <Typography color="text.secondary" variant="subHeadingM">
            {tagline2}
          </Typography>
        </Grid>
        <Grid order={2} size={{ md: 6.5, xs: 12 }}>
          <Grid container columns={10} spacing={4}>
            {menuItemsWithSublink?.map((item) => (
              <Grid key={item.headerLabel} size={{ sm: 2, xs: 3 }}>
                <List disablePadding spacing={1}>
                  <ListItem>
                    <ListItemPrimaryText gutterBottom color="text.secondary" variant="buttonM">
                      {item?.headerLabel}
                    </ListItemPrimaryText>
                  </ListItem>
                  {item?.sublinks?.map((sublink) => (
                    <ListItem key={sublink?.sublinkLabel}>
                      <NavigationItem
                        color="text.dark"
                        href={sublink?.sublinkUrl}
                        variant="paragraphBodyS"
                      >
                        {sublink?.sublinkLabel}
                      </NavigationItem>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            ))}
            <Grid size={{ sm: 2, xs: 3 }}>
              <List disablePadding spacing={1}>
                <ListItem>
                  <ListItemPrimaryText gutterBottom color="text.secondary" variant="buttonM">
                    {footerLabels?.products}
                  </ListItemPrimaryText>
                </ListItem>
                <NavigationItem color="text.dark" href="/" variant="paragraphBodyS">
                  {footerLabels?.business}
                </NavigationItem>
                <NavigationItem color="text.dark" href="/personal" variant="paragraphBodyS">
                  {footerLabels?.personal}
                </NavigationItem>
              </List>
            </Grid>
          </Grid>
        </Grid>
        <Grid order={3} size={{ md: 3.5, xs: 12 }}>
          <Typography gutterBottom color="text.secondary" component="div" variant="buttonM">
            {footerLabels?.subscribe}
          </Typography>
          {disclaimer && <NewsletterForm disclaimer={disclaimer} />}
        </Grid>
        <Grid
          alignItems="flex-end"
          display="flex"
          order={{ md: 4, xs: 5 }}
          paddingY={1}
          size={{ md: 2, xs: 6 }}
        >
          <AppLogo />
        </Grid>
        <Grid
          display="flex"
          flexDirection="column"
          order={{ md: 5, xs: 4 }}
          size={{ md: 8.5, xs: 12 }}
        >
          <Typography color="text.secondary" variant="buttonM">
            {footerLabels?.followUs}
          </Typography>
          {socialMenu?.socialLinks && <SocialMenu items={socialMenu?.socialLinks} />}
          {legalPrefix && <Typography variant="caption">{legalPrefix}</Typography>}
          <Typography variant="caption">
            Copyright © {currentYear} {legalCompanyName}
          </Typography>
        </Grid>
        <Grid
          alignItems="flex-start"
          display="flex"
          justifyContent="flex-end"
          order={6}
          size={{ md: 1.5, xs: 6 }}
        >
          <BackToTopButton />
        </Grid>
      </Grid>
    </Container>
  );
}
